'use client';

import { useEffect } from 'react';

import {
  SIGNUP_ATTR_COOKIE,
  SIGNUP_ATTR_COOKIE_MAX_AGE_SECONDS,
  buildAttributionFromRequest,
  decodeAttrCookie,
  encodeAttrCookie,
  type SignupSource,
} from '@/lib/analytics/signup-source-config';

/**
 * Persist UTM / referral / cold-email params (and Google Ads click ids) on first
 * landing so they survive the auth handoff.
 *
 * - Writes the canonical `wisprs_attr` cookie in the SAME versioned `{v:1,…}` shape
 *   as middleware and <AttributionCapture> (via `encodeAttrCookie`). Historically
 *   this component wrote a divergent FLAT shape, which `decodeAttrCookie` rejected
 *   and which silently broke both signup_source stamping and cold-email cl-token
 *   linkage whenever this writer won the first-touch race. One shape now, no
 *   collision.
 * - Strictly first-touch. Middleware already reads all five supported paid click
 *   ids from the initial request. If this component sees an existing cookie it
 *   never merges a later campaign into it: doing so silently turns last-touch
 *   data into first-touch data. Later campaign URLs and external referrers stay
 *   in the session page-view event stream instead.
 *
 * Mount once in the root layout.
 */
function browserTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

export function UtmCapture() {
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tz = browserTimezone();

      const existing = decodeAttrCookie(readCookie(SIGNUP_ATTR_COOKIE));

      // Timezone enrichment: middleware writes the cookie server-side and
      // cannot know the browser's timezone — the one location signal a
      // corporate proxy/VPN can't fake. Add it once; touch nothing else
      // (first-touch fields stay exactly as written).
      if (existing) {
        if (tz && !existing.tz) writeAttrCookie({ ...existing, tz });

        return;
      }

      // Classify the landing, including untagged extension auth handoffs.

      const source: SignupSource = buildAttributionFromRequest({
        searchParams: params,
        referer: document.referrer || null,
        selfHost: window.location.host,
        landing: window.location.pathname,
        capturedAt: new Date().toISOString(),
      }) ?? {
        channel: 'direct',
        attributionScope: 'first-touch',
        sourceEvidence: 'no-referrer',
        landing: window.location.pathname,
        capturedAt: new Date().toISOString(),
      };

      if (
        source.channel !== 'extension' &&
        !source.utm &&
        !source.ref &&
        !source.cl &&
        !Object.keys(source.clickIds ?? {}).length
      )
        return;

      writeAttrCookie(tz ? { ...source, tz } : source);
    } catch {
      // never break the page
    }
  }, []);

  return null;
}

function writeAttrCookie(source: SignupSource): void {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${SIGNUP_ATTR_COOKIE}=${encodeAttrCookie(source)}; Max-Age=${SIGNUP_ATTR_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));

  return m ? decodeURIComponent(m[1]) : null;
}
